import type { ComponentProps } from 'react'
import React from 'react'
import { Link } from 'react-router-dom'

import { ModalBox, Skeleton, useModalContext } from '@fourthwall/components'

import * as S from './TilesModal.styled'

const getComponent = ({ to, href }: Pick<TileModalItemProps, 'to' | 'href'>) => {
  if (to) {
    return Link
  }
  if (href) {
    return 'a'
  }

  return 'button'
}

const ITEM_DEFAULT_WIDTH = 288

export type TileModalItemProps = {
  image: string
  imageAlt: string
  heading: string
  subheading?: string
  to?: string
  href?: string
  onClick?: () => void
  children?: React.ReactNode
}

export interface TilesModalProps extends Omit<ComponentProps<typeof ModalBox>, 'children'> {
  heading?: string
  children: React.ReactNode
  isLoading?: boolean
  itemsCount?: number
}

export const TilesModal = ({
  children,
  isLoading,
  heading,
  itemsCount = 3,
  ...props
}: TilesModalProps) => {
  const { close } = useModalContext()

  return (
    <ModalBox maxWidth={itemsCount * ITEM_DEFAULT_WIDTH} onClose={close} title={heading} {...props}>
      {isLoading ? (
        <TilesModalSkeleton itemsCount={itemsCount} />
      ) : (
        <S.Container>{children}</S.Container>
      )}
    </ModalBox>
  )
}

const TilesModalItem = ({
  to,
  href,
  image,
  imageAlt,
  heading,
  subheading,
  onClick,
  children,
}: TileModalItemProps) => {
  const { close } = useModalContext()

  return (
    <S.Item as={getComponent({ to, href })} to={to} onClick={onClick || close}>
      <S.ItemImageContainer>
        <S.Image alt={imageAlt} src={image} />
      </S.ItemImageContainer>
      <S.Content>
        <S.Heading type="h7" weight="semibold">
          {heading}
        </S.Heading>
        {subheading && <S.Subheading>{subheading}</S.Subheading>}
        {children}
      </S.Content>
    </S.Item>
  )
}

const TilesModalFooter = ({ children }: { children: React.ReactNode }) => {
  return <div>{children}</div>
}

const TilesModalItems = ({ children }: { children: React.ReactNode }) => {
  return <S.Items>{children}</S.Items>
}

const TilesModalSkeleton = ({ itemsCount }: { itemsCount: number }) => (
  <S.Container>
    <S.Items>
      {Array.from({ length: itemsCount }, (_, index) => (
        <S.SkeletonItem key={`skeleton-${index}`}>
          <S.ItemImageContainer>
            <Skeleton.Shape width="100%" height="100%" />
          </S.ItemImageContainer>
          <S.Content>
            <Skeleton gap={8}>
              <Skeleton.Shape width="80%" height={18} />
              <Skeleton.Shape width="60%" height={14} />
            </Skeleton>
          </S.Content>
        </S.SkeletonItem>
      ))}
    </S.Items>
  </S.Container>
)

TilesModal.Item = TilesModalItem
TilesModal.Items = TilesModalItems
TilesModal.Footer = TilesModalFooter
